import * as React from 'react';
import { Select } from '@SBGSports/catapult-core';
import { SelectMenuItem } from '@SBGSports/catapult-core/dist/components/select/__commons__/selectCommons';
import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import { useHasFeature } from 'api';
import { FrontendConfigFeaturesEnum } from 'oas';
import { useTranslations } from 'i18n';
import { EVENTS } from '../../analytics/analytics-events';
import { useReportTypeStore } from 'shared/state/reporting/reportTypeStore';
import { REPORT_TYPE } from '../constants';
import { BaseReportType } from 'reporting/types';

export const ReportSelector: React.FC = () => {
    const { activityId } = useParams<{ type: string; activityId: string }>();
    const { __ } = useTranslations();
    const history = useHistory();
    const location = useLocation();

    const proReporting = useHasFeature(FrontendConfigFeaturesEnum.ProReporting);

    const { reportType, setReportType } = useReportTypeStore();
    const isOnReportingPage = location.pathname.startsWith('/reporting');

    useEffect(() => {
        mixpanel?.time_event(EVENTS.reporting.reportTypeSelection);
        return () => {
            mixpanel?.track(EVENTS.reporting.reportTypeSelection, { reportType });
        };
    }, [reportType]);

    const reportOptions: SelectMenuItem[] = useMemo(() => {
        const isSelected = (type: BaseReportType) => isOnReportingPage && reportType === type;

        const reports: SelectMenuItem[] = [
            {
                id: REPORT_TYPE.activity,
                value: __('misc.activity'),
                isSelected: isSelected(REPORT_TYPE.activity),
                testId: 'group-option-activity',
            },
            {
                id: REPORT_TYPE.athlete,
                value: __('reporting.athlete_in_activity'),
                isSelected: isSelected(REPORT_TYPE.athlete),
                testId: 'group-option-athlete-in-activity',
            },
            {
                id: REPORT_TYPE.longitudinal,
                value: __('reporting.longitudinal'),
                isSelected: isSelected(REPORT_TYPE.longitudinal),
                testId: 'group-option-longitudinal',
            },
            {
                id: REPORT_TYPE.match_day,
                value: __('reporting.match_day'),
                isSelected: isSelected(REPORT_TYPE.match_day),
                testId: 'group-option-match-day',
            },
            {
                id: REPORT_TYPE.weekly,
                value: __('reporting.weekly'),
                isSelected: isSelected(REPORT_TYPE.weekly),
                testId: 'group-option-weekly',
            },
        ];

        if (proReporting) {
            //add status to top of report selector list
            reports.unshift({
                id: REPORT_TYPE.status,
                value: __('reporting.status'),
                isSelected: isSelected(REPORT_TYPE.status),
                testId: 'group-option-status',
            });
        }

        return reports;
    }, [__, reportType, proReporting, isOnReportingPage]);

    const reportRoot = '/reporting';

    const handleReportSelected = useCallback(
        (selectedOption: SelectMenuItem) => {
            const selected = selectedOption.id as BaseReportType;

            if (!selected || !setReportType) {
                return;
            }

            const isSingleActivityReport = selected === REPORT_TYPE.activity || selected === REPORT_TYPE.athlete;

            // Build search params for destination — preserve filters for single-activity reports
            const searchParams = new URLSearchParams(location.search);
            searchParams.delete('activities');

            if (!isSingleActivityReport) {
                searchParams.delete('date-from');
                searchParams.delete('date-to');
            }

            if (
                selected === REPORT_TYPE.status ||
                selected === REPORT_TYPE.longitudinal ||
                selected === REPORT_TYPE.weekly
            ) {
                searchParams.delete('tags');
            }

            // Update report type state
            setReportType(selected);

            // Build path
            let url = `${reportRoot}/${selected}`;
            if (selected !== REPORT_TYPE.status && selected !== REPORT_TYPE.longitudinal && activityId) {
                url = url.concat('/' + activityId);
                if (selected === REPORT_TYPE.athlete) {
                    url = url.concat('/athlete');
                }
            }

            // Navigate with cleaned search params
            const searchStr = searchParams.toString();
            history.push(`${url}${searchStr ? '?' + searchStr : ''}`);
        },
        [activityId, history, location.search, setReportType],
    );

    return (
        <Select
            id="report-type-filter"
            testId="report-type-filter"
            value={__('reporting.reporting')}
            middleMenuItems={reportOptions}
            onItemSelect={handleReportSelected}
            size="sm"
        />
    );
};
